import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useVersionGateState } from "@/zustand/VersionGateState";
import { useTranslation } from "react-i18next";
import appVersion from "@/config/version";
import { safeSession } from "@/utils/safeStorage";

const REFRESH_ATTEMPTED_KEY = "version-gate-refresh-attempted";

// iOS Safari throws SecurityError ("The operation is insecure.") — or returns a
// null `sessionStorage` — when storage is blocked or partitioned (private mode,
// "Prevent Cross-Site Tracking", disabled site data). safeSession guards every
// access so the gate degrades gracefully instead of crashing the app.

function handleRefresh() {
  safeSession.set(REFRESH_ATTEMPTED_KEY, appVersion);
  window.location.reload();
}

const preventDismiss = (event: Event) => {
  event.preventDefault();
};

const VersionGate = () => {
  const updateRequired = useVersionGateState((s) => s.updateRequired);
  const { t } = useTranslation();

  if (!updateRequired) {
    return null;
  }

  const refreshAlreadyAttempted =
    safeSession.get(REFRESH_ATTEMPTED_KEY) === appVersion;

  return (
    <DialogPrimitive.Root open modal>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[9998] bg-background" />
        <DialogPrimitive.Content
          onEscapeKeyDown={preventDismiss}
          onPointerDownOutside={preventDismiss}
          onInteractOutside={preventDismiss}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-background focus:outline-none"
        >
          <div className="mx-auto flex max-w-md flex-col items-center gap-6 px-6 text-center">
            <div className="flex size-16 items-center justify-center rounded-full bg-primary/10">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-primary"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </div>

            <DialogPrimitive.Title className="text-2xl font-semibold text-foreground">
              {t("versionGate.title")}
            </DialogPrimitive.Title>

            {refreshAlreadyAttempted ? (
              <>
                <DialogPrimitive.Description className="text-muted-foreground">
                  {t("versionGate.retryDescription")}
                </DialogPrimitive.Description>
                <ol className="list-inside list-decimal text-left text-sm text-muted-foreground">
                  <li>{t("versionGate.retryStep1")}</li>
                  <li>{t("versionGate.retryStep2")}</li>
                </ol>
                <button
                  onClick={handleRefresh}
                  className="rounded-lg bg-primary px-8 py-3 font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  {t("versionGate.retryButton")}
                </button>
                <p className="text-sm text-muted-foreground">
                  {t("versionGate.contactInfo")}
                </p>
              </>
            ) : (
              <>
                <DialogPrimitive.Description className="text-muted-foreground">
                  {t("versionGate.description")}
                </DialogPrimitive.Description>
                <button
                  onClick={handleRefresh}
                  className="rounded-lg bg-primary px-8 py-3 font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  {t("versionGate.refreshButton")}
                </button>
              </>
            )}

            <p className="text-xs text-muted-2">
              {t("versionGate.currentVersion", { version: appVersion })}
            </p>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default VersionGate;
